import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  href: string;
  checkFn: () => Promise<boolean>;
}

export function OnboardingChecklist() {
  const { user } = useAuth();
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const checklistItems: ChecklistItem[] = [
    {
      id: "nexis-script",
      label: "Create your first Nexis script",
      description: "Generate viral-ready scripts with AI",
      href: "/app/nexis-ai",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("nexis_projects")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id);
        return (count || 0) > 0;
      },
    },
    {
      id: "generate-prompts",
      label: "Generate image or video prompts",
      description: "Get AI prompts for your visuals",
      href: "/app/nexis-ai",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("nexis_scenes")
          .select("*, nexis_projects!inner(user_id)", { count: "exact", head: true })
          .eq("nexis_projects.user_id", user.id)
          .not("image_prompt", "is", null);
        return (count || 0) > 0;
      },
    },
    {
      id: "build-product",
      label: "Build your first product",
      description: "Turn your expertise into something sellable",
      href: "/app/monetization-ai",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("monetization_projects")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id);
        return (count || 0) > 0;
      },
    },
    {
      id: "sales-page",
      label: "Create a sales page",
      description: "Get a conversion-optimised page in 60 seconds",
      href: "/app/sales-page-builder",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("monetization_products")
          .select("*, monetization_projects!inner(user_id)", { count: "exact", head: true })
          .eq("monetization_projects.user_id", user.id)
          .eq("product_type", "sales-page");
        return (count || 0) > 0;
      },
    },
    {
      id: "email-funnel",
      label: "Set up your email funnel",
      description: "Start collecting emails and nurturing leads",
      href: "/app/email-list-builder",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("conversations")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id);
        // Check if they've used the email list builder tool
        const { count: emailCount } = await supabase
          .from("credit_transactions")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("action_type", "email-list-builder");
        return (emailCount || 0) > 0;
      },
    },
    {
      id: "launch-plan",
      label: "Get your launch plan",
      description: "Know exactly what to post every day for 7 days",
      href: "/app/launch-advisor",
      checkFn: async () => {
        if (!user) return false;
        const { count } = await supabase
          .from("credit_transactions")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("action_type", "launch-advisor");
        return (count || 0) > 0;
      },
    },
  ];

  useEffect(() => {
    const checkProgress = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      const isDismissed = localStorage.getItem(`onboarding-dismissed-${user.id}`);
      if (isDismissed) {
        setDismissed(true);
        setLoading(false);
        return;
      }

      const completed = new Set<string>();
      for (const item of checklistItems) {
        const isComplete = await item.checkFn();
        if (isComplete) completed.add(item.id);
      }
      setCompletedSteps(completed);
      setLoading(false);
    };

    checkProgress();
  }, [user]);

  const handleDismiss = () => {
    if (user) {
      localStorage.setItem(`onboarding-dismissed-${user.id}`, "true");
    }
    setDismissed(true);
  };

  if (loading || dismissed || completedSteps.size === checklistItems.length) {
    return null;
  }

  const progress = Math.round((completedSteps.size / checklistItems.length) * 100);

  return (
    <div className="glass-card p-6 border-primary/20 relative">
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-3 right-3 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
        onClick={handleDismiss}
      >
        <X className="w-4 h-4" />
      </Button>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary-foreground" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold">Getting Started</h2>
          <p className="text-sm text-muted-foreground">
            {completedSteps.size} of {checklistItems.length} completed — keep going!
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">Progress</span>
          <span className="text-foreground font-medium">{progress}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2">
        {checklistItems.map((item) => {
          const isComplete = completedSteps.has(item.id);
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                isComplete
                  ? "bg-green-500/10 border border-green-500/20"
                  : "bg-muted/30 hover:bg-muted/50 border border-transparent"
              }`}
            >
              {isComplete ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isComplete ? "text-green-400" : "text-foreground"}`}>
                  {item.label}
                </p>
                <p className="text-xs text-muted-foreground truncate">{item.description}</p>
              </div>
              {!isComplete && (
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
